<template>
  <div>
    <PageHeader title="Commissions" />
    <div class="mb-4 flex flex-col sm:flex-row justify-between gap-y-4 sm:gap-y-0">
      <div class="flex flex-col sm:flex-row w-full gap-y-4 sm:gap-10 items-center">
        <MonthlyDateFilterSelector
          class="w-1/4"
          placeholder="Sélectionner une période"
          @newDateSelected="updateDateFilters"
        />
        <SearchBarFilter
          v-for="(filterCategory, filtersName, filterIndex) in availableFiltersWithOptions"
          :key="filterIndex"
          :filterName="filtersName"
          :filterIdx="filterIndex"
          :current-page="currentPage"
          :available-filters="filters"
          :selected-filters="currentFilteringData"
          @updateFilterSearch="updateCurrentFilteringData"
        />
      </div>
      <SecondaryButton @click="exportSummary">Exporter</SecondaryButton>
    </div>
    <IbanMissing v-if="ibanMissing" />
    <div>
      <Table
        :columns="columns"
        :data="invoices"
        is-accessible
        @view="redirectToInvoiceDetailsPage"
        :selectableRowFilter="selectableRowFilter"
      >
        <template #paymentStatus="{ row }">
          <FieldSpan :style-to-add="paymentStatusFieldDisplayStyle(row.paymentStatus)" :value="$t(row.paymentStatus)" />
        </template>
        <template #amount="{ row }">
          {{ formatPrice(row.amount) }}
        </template>
      </Table>
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        :totalCount="totalCount"
        @changePage="handleChangePage"
      />
    </div>
  </div>
</template>

<script>
import AdminService from "../../../api/services/admin";
import MonthlyDateFilterSelector from "@/components/menu/MonthlyDateFilterSelector.vue";
import IbanMissing from "@/components/IbanMissing.vue";
import { Pagination, SecondaryButton, SearchBarFilter, PageHeader, Table } from "@estaly/ui";
import FieldSpan from "@/components/utils/FieldSpan.vue";
import { mapActions } from "vuex";
import { setQueryParams, getQueryParams, arrayFilterIfPresent } from "@estaly/ui/src/utils/query_params.js";
import download from "@estaly/ui/src/utils/download";
import { formatPrice } from "@estaly/ui/src/utils/price_formatter";

export default {
  components: {
    Pagination,
    SearchBarFilter,
    MonthlyDateFilterSelector,
    IbanMissing,
    SecondaryButton,
    PageHeader,
    Table,
    FieldSpan,
  },

  computed: {
    availableFiltersWithOptions() {
      return Object.fromEntries(
        Object.entries(this.filters).filter(([filterKey, filterOptions]) => filterKey && filterOptions?.length > 1),
      );
    },
  },

  data() {
    return {
      invoices: [],
      filters: {
        organizations: [],
        stores: [],
        payment_statuses: [],
        transaction_types: [],
      },
      totalPages: null,
      totalCount: null,
      currentPage: 1,
      currentFilteringData: {
        organizations: [],
        stores: [],
        payment_statuses: [],
        transaction_types: [],
        payment_start_date: null,
        payment_end_date: null,
      },
      activeTab: 1,
      billAmount: null,
      statementAmount: null,
      columns: [
        { label: "Date de versement", field: "paymentDate" },
        { label: "Entité", field: "salesChannel" },
        { label: "Type", field: "transactionType" },
        { label: "Libellé", field: "friendlyId" },
        { label: "Montant", slot: "amount", field: "amount" },
        { label: "Statut paiement", slot: "paymentStatus", field: "status" },
      ],
    };
  },

  mounted() {
    this.initQueryParams();
    this.retrieveInvoices();
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    formatPrice,

    paymentStatusFieldDisplayStyle(paymentStatus) {
      switch (paymentStatus) {
        case "pending_payment":
          return "bg-yellow-100 text-yellow-800";
        case "paid":
          return "bg-green-100 text-green-800";
        default:
          return "bg-blue-100 text-blue-800";
      }
    },

    handleChangePage(page) {
      this.currentPage = page;
      this.retrieveInvoices();
    },

    updateCurrentFilteringData(filterName, filterOptions) {
      this.currentFilteringData[filterName] = filterOptions;
      this.handleChangePage(1);
    },

    updateDateFilters(startDate, endDate) {
      this.currentFilteringData["payment_start_date"] = startDate;
      this.currentFilteringData["payment_end_date"] = endDate;
      this.handleChangePage(1);
    },

    async retrieveInvoices() {
      setQueryParams({ page: this.currentPage, ...this.currentFilteringData });
      const response = await AdminService.getInvoices(this.currentPage, this.currentFilteringData);
      if (!response?.success) return;

      this.invoices = response.data.invoices;
      this.filters = response.data.filters;
      this.billAmount = response.data.billAmount;
      this.statementAmount = response.data.statementAmount;
      this.ibanMissing = response.data.ibanMissing;
      this.totalCount = response.data.page.totalCount;
      this.totalPages = response.data.page.totalPages;
    },

    redirectToInvoiceDetailsPage(invoice) {
      this.$router.push(`/admin/billing/invoices/${invoice.friendlyId}`);
    },

    initQueryParams() {
      const queryParams = getQueryParams();
      this.currentFilteringData = {
        organizations: arrayFilterIfPresent(queryParams.organizations),
        stores: arrayFilterIfPresent(queryParams.stores),
        payment_statuses: arrayFilterIfPresent(queryParams.payment_statuses),
        transaction_types: arrayFilterIfPresent(queryParams.transaction_types),
        start_date: queryParams.start_date,
        end_date: queryParams.end_date,
      };
      this.currentPage = queryParams.page || 1;
    },

    async exportSummary() {
      const paymentStartDate = this.currentFilteringData["payment_start_date"];
      const paymentEndDate = this.currentFilteringData["payment_end_date"];

      const params = { paymentStartDate, paymentEndDate };

      const response = await AdminService.getInvoicesSummary(params);
      if (!response?.success) return;

      const filename =
        paymentStartDate && paymentEndDate
          ? `invoices_summary_${this.$d(new Date(paymentStartDate), "long")}_${this.$d(new Date(paymentEndDate), "long")}.csv`
          : `invoices_summary.csv`;
      download(response.data, filename);

      this.notify({
        category: "simple",
        type: "success",
        title: "Exporting invoices summary file.",
      });
    },
  },
};
</script>
